import React, { useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Checkbox, Grid, Paper } from "@material-ui/core";
import Condition from "Component/Condition";
import VirtualizedTable, { ColumnData } from "Common/Component/VirtualizedTable";
import { useCallback } from "react";
import { callWebApi } from "Common/Utility/Api";
import { useFetch, useExecute } from "Hooks/useFetch";
import { LoadingMode, useLoadingElement } from "Component/Loading";
import { CancelTokenSource } from "axios";
import { useGenericStyles } from "Common/Utility/Styles";
import { useContentHeight } from "Hooks/useResize";
import TextField from "Component/TextField";
import { Design, VisibleColumn } from "Common/Utility/Constants";
import { useInputManager } from "Common/Utility/HandleUtility";
import { PublicDocument } from "Models/PublicDocument";
import { DateUtility } from "Common/Utility/DateUtility";
import { useColumnControl } from "Hooks/useColumnControl";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "100%",
    height: (props: any) => props.height,
  },
}));

export const PublicManager = () => {
  const [height, conditionRef] = useContentHeight(
    Design.componentUnitHeight + Design.margin + Design.componentUnitHeight
  );

  const classes = useStyles({ height: height });

  const genericClasses = useGenericStyles();

  const [viewData, setViewData] = useState<PublicDocument[]>([]);

  const [condition, setCondition] = useState({ fileName: "", companyName: "" });

  const [search, setSearch] = useState({ fileName: "", companyName: "" });

  const inputManager = useInputManager(setCondition);

  const loadingElement = useLoadingElement(
    classes.paper,
    LoadingMode.Circular,
    useFetch(
      useCallback(
        async (signal: CancelTokenSource) => {
          const response = await callWebApi().get<PublicDocument[]>("/publicdocuments", {
            cancelToken: signal.token,
            params: {
              filename: search.fileName,
              companyname: search.companyName,
            },
          });

          if (response.data == null) {
            return;
          }

          response.data.forEach((element) => {
            element.checked = false;
          });
          setViewData(response.data);
        },
        [search]
      ),
      false
    )
  );

  const downloadLink = useRef<HTMLAnchorElement>(null);

  const executeDownload = useExecute(
    useCallback(async (unmounted: { value: boolean }, object: { downloadIdList: PublicDocument[] }) => {
      for (let i = 0; i < object.downloadIdList.length; i++) {
        if (object.downloadIdList[i].checked) {
          const response = await callWebApi().get("/publicdocuments/file", {
            params: {
              fileId: object.downloadIdList[i].fileId,
            },
            responseType: "blob",
          });

          if (unmounted.value) {
            return;
          }

          if (downloadLink.current) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            downloadLink.current.href = url;
            downloadLink.current.setAttribute("download", object.downloadIdList[i].fileName);
            downloadLink.current.click();
          }
        }
      }
    }, [])
  );

  const handleOnClickDownLoad = useCallback(() => {
    executeDownload({ downloadIdList: viewData });
  }, [viewData, executeDownload]);

  const [getVisible, handleOnCloseContextMenu, handleOnChangeHeaderVisible] = useColumnControl(
    VisibleColumn.PublicDocumentOpen
  );

  const columns: ColumnData[] = useMemo(() => {
    return [
      {
        width: 80,
        label: "",
        headerAlign: "center",
        bodyAlign: "center",
        widthType: "fix",
        component: (data: PublicDocument, rowIndex: number) => {
          return (
            <Checkbox
              color="primary"
              checked={data.checked}
              onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                setViewData((value) => {
                  data.checked = checked;
                  return [...value];
                });
              }}
            />
          );
        },
      },
      {
        width: 600,
        label: "ファイル名",
        dataKey: "fileName",
        headerAlign: "center",
        bodyAlign: "left",
        fit: true,
        visible: getVisible(0),
      },
      {
        width: 300,
        label: "公開企業",
        dataKey: "companyName",
        headerAlign: "center",
        bodyAlign: "left",
        visible: getVisible(1),
      },
      {
        width: 300,
        label: "登録日時",
        dataKey: "createdAt",
        headerAlign: "center",
        bodyAlign: "left",
        convert: (data: any) => DateUtility.format(data, "yyyy/MM/dd HH:mm:ss"),
        visible: getVisible(2),
      },
    ];
  }, [getVisible]);

  const checked = useMemo(() => {
    return viewData.findIndex((i) => i.checked) >= 0;
  }, [viewData]);

  return (
    <>
      <a href="#dummy" style={{ display: "none" }} ref={downloadLink}>
        DownloadLink
      </a>

      <Grid container direction="row" justify="center" alignItems="flex-start" spacing={1}>
        <Grid item xs={12}>
          <Condition observer={conditionRef}>
            <>
              <TextField
                className={genericClasses.margin}
                label="ファイル名"
                value={condition.fileName}
                onChange={inputManager.handleOnChange("fileName")}
              />
              <TextField
                className={genericClasses.margin}
                label="企業名"
                value={condition.companyName}
                onChange={inputManager.handleOnChange("companyName")}
              />
              <Button
                className={genericClasses.margin}
                variant="contained"
                color="primary"
                onClick={() => setSearch({ ...condition })}
              >
                検索
              </Button>
              <Button
                className={genericClasses.margin}
                variant="outlined"
                onClick={() => setCondition({ fileName: "", companyName: "" })}
              >
                クリア
              </Button>
            </>
          </Condition>
        </Grid>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            {loadingElement ?? (
              <VirtualizedTable
                values={viewData}
                setValues={setViewData}
                tableHeight={height}
                rowHeight={48}
                columns={columns}
                onCloseContextMenu={handleOnCloseContextMenu}
                onChangeHeaderVisible={handleOnChangeHeaderVisible}
                headerContext
              />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} className={genericClasses.textAlignRight}>
          <Button
            className={genericClasses.margin}
            variant="contained"
            color="primary"
            onClick={handleOnClickDownLoad}
            disabled={!checked}
          >
            ダウンロード
          </Button>
        </Grid>
      </Grid>
    </>
  );
};
